import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, Bot, Cable, Hash, KeyRound, LogOut, Megaphone, PhoneCall, ScrollText, Settings as SettingsIcon, Users, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: Activity, testid: "nav-dashboard" },
  { to: "/trunks", label: "Trunks", icon: Cable, testid: "nav-trunks" },
  { to: "/agents", label: "Agents", icon: Bot, testid: "nav-agents" },
  { to: "/numbers", label: "Numbers", icon: Hash, testid: "nav-numbers" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/calls", label: "Calls", icon: PhoneCall, testid: "nav-calls" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/worker-keys", label: "Worker Keys", icon: KeyRound, testid: "nav-worker-keys" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className="flex w-60 shrink-0 flex-col border-r border-border bg-card"
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2 px-5 py-5 border-b border-border">
          <Waves className="h-5 w-5 text-primary" />
          <div className="text-base font-semibold tracking-tight">VoxIVR</div>
          <div className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
            Admin
          </div>
        </div>
        <nav className="flex flex-col p-3 gap-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-secondary text-secondary-foreground font-medium"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto p-3 text-xs text-muted-foreground">
          Phase 1 · v0.1.0
        </div>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col">
        <header
          className="flex items-center justify-between border-b border-border px-6 py-3 bg-card/60 backdrop-blur"
          data-testid="topbar"
        >
          <div className="text-sm text-muted-foreground">Admin Console</div>
          <div className="flex items-center gap-4">
            <span className="text-sm" data-testid="topbar-username">
              <span className="text-muted-foreground">signed in as </span>
              <span className="font-medium">{user?.username || "—"}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              data-testid="topbar-logout"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6 lg:p-10" data-testid="page-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
